import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid3x3, Loader2, Mic, MicOff, Moon, Phone, PhoneIncoming, PhoneOff } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { initialsOf, StatusPill } from "@/components/patterns";
import { cn } from "@/lib/utils";
import { useSoftphone } from "./SoftphoneContext";
import { CallOutcomeDialog, type OutcomeTarget } from "./CallOutcomeDialog";
import { LinkCaller } from "./LinkCaller";
import { dateLocale, useDisplayKey, withClock } from "@/lib/dateTime";

const mmss = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const clockTime = (iso: string) => new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "numeric", minute: "2-digit" }));
interface TeamMember { id: string; name: string; status: "AVAILABLE" | "ON_CALL" | "WRAP_UP" | "AWAY" | "OFFLINE"; away_until: string | null; call_sec: number | null; wrap_left_sec: number | null }
const TEAM_DOT: Record<TeamMember["status"], string> = { AVAILABLE: "bg-success", ON_CALL: "bg-sidebar", WRAP_UP: "bg-warning", AWAY: "bg-warning", OFFLINE: "bg-muted-foreground/40" };
const teamText = (m: TeamMember) =>
  m.status === "AVAILABLE" ? "Available" : m.status === "ON_CALL" ? `On a call${m.call_sec != null ? ` ${Math.floor(m.call_sec / 60)}:${String(m.call_sec % 60).padStart(2, "0")}` : ""}` : m.status === "WRAP_UP" ? "Wrap-up" : m.status === "AWAY" ? `Away${m.away_until ? ` · ${clockTime(m.away_until)}` : ""}` : "Offline";
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

/**
 * Calling in the top bar. Idle it is a small status dot with a phone (nothing floats over the page); during a call it
 * becomes a chip with the name and a timer, and opens a small panel with mute, keypad and hang up. When a call to a
 * record ends it asks how it went, once per call.
 */
export function CallChip() {
  useDisplayKey(); // re-render when the clock style or the date order changes
  const sp = useSoftphone();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const [open, setOpen] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const baseline = useRef(0);
  const [keypad, setKeypad] = useState(false);
  const [outcome, setOutcome] = useState<OutcomeTarget | null>(null);
  const askedFor = useRef<string | null>(null);

  const inCall = sp.phase === "dialing" || sp.phase === "ringing" || sp.phase === "live";
  const ringingIn = sp.phase === "incoming";

  // Ask how the call went when it ends (test calls have nothing to record)
  useEffect(() => {
    const c = sp.call;
    if (sp.phase === "ended" && c && c.recordType && c.recordType !== "TEST" && askedFor.current !== c.callId) {
      askedFor.current = c.callId;
      setOutcome({ callId: c.callId, label: c.label, sub: c.sub });
      setOpen(false);
    }
  }, [sp.phase, sp.call]);

  const ended = sp.phase === "ended";
  const idle = !inCall && !ended && !ringingIn;
  const canTeam = hasPermission("report.view_team");

  // A call opens the panel by itself; when it is over the panel closes
  useEffect(() => {
    if (inCall || ringingIn) setOpen(true);
    else setKeypad(false);
  }, [inCall, ringingIn]);
  useEffect(() => {
    if (!inCall && !ringingIn && !ended) setOpen(false);
  }, [inCall, ringingIn, ended]);

  // Customer calls missed while the rep is on this call are counted from when it began
  useEffect(() => {
    if (inCall) baseline.current = sp.missedCallbacks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inCall]);

  // A head sees who on their team can take calls right now (refreshed while the panel is open)
  useEffect(() => {
    if (!open || !idle || !canTeam) return;
    let live = true;
    const load = () => api.get("/api/Call/TeamStatus").then((r) => live && setTeam(unwrapObject<TeamMember[]>(r) || [])).catch(() => undefined);
    void load();
    const t = setInterval(load, 15000);
    return () => { live = false; clearInterval(t); };
  }, [open, idle, canTeam, sp.liveTick]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      error("Couldn't change your status", err?.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };
  const go = (to: string) => { setOpen(false); navigate(to); };

  const closeOutcome = (saved: boolean) => {
    setOutcome(null);
    void sp.refreshPending();
    if (saved && sp.phase === "ended") sp.dismiss();
  };

  if (sp.phase === "off") return null;

  const away = sp.rep.status === "AWAY";
  const wrapping = ended && sp.wrapLeft > 0;
  const missedDuring = inCall || ended ? Math.max(0, sp.missedCallbacks - baseline.current) : 0;
  const dot = sp.phase === "ready" ? (away ? "bg-warning" : "bg-success") : sp.phase === "offline" ? "bg-muted-foreground/50" : inCall ? "bg-success" : "bg-warning";
  const tip = sp.phase === "ready" ? (away ? "Away: your customers ring your team" : "Calling ready") : sp.phase === "connecting" ? "Connecting to the phone line…" : sp.phase === "offline" ? "Calling offline. Open My calling to see why." : "On a call";
  const firstName = (sp.call?.label || "").replace(/^Test call to your mobile$/, "Test call").split(" ")[0];
  const badge = sp.pending.length + sp.missedCallbacks;

  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setChoosing(false); }}>
        <PopoverTrigger asChild>
          {ringingIn ? (
            <button type="button" aria-label={`Incoming call from ${sp.call?.label || ""}`} className="inline-flex h-8 animate-pulse items-center gap-2 rounded-full bg-warning-soft px-3 text-[12.5px] font-semibold text-warning">
              <PhoneIncoming className="size-3.5" />
              Incoming · {sp.call?.label || "Unknown number"}
            </button>
          ) : inCall || ended ? (
            <button
              type="button"
              aria-label={ended ? "Call ended" : `On a call with ${sp.call?.label || ""}`}
              className={cn("inline-flex h-8 items-center gap-2 rounded-full px-3 text-[12.5px] font-semibold", wrapping ? "bg-warning-soft text-warning" : sp.phase === "live" || ended ? "bg-sidebar text-white" : "bg-warning-soft text-warning")}
            >
              <span className={cn("size-2 rounded-full", sp.phase === "live" ? "bg-success" : "bg-warning")} />
              {wrapping ? (
                <>
                  Wrap-up · <span className="font-mono">{Math.floor(sp.wrapLeft / 60)}:{String(sp.wrapLeft % 60).padStart(2, "0")}</span>
                </>
              ) : sp.phase === "dialing" ? `Calling ${firstName}…` : sp.phase === "ringing" ? `Ringing ${firstName}…` : (
                <>
                  {firstName} · <span className="font-mono">{mmss(sp.seconds)}</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              title={tip}
              aria-label={`${tip}${sp.pending.length ? `, ${sp.pending.length} outcomes to log` : ""}${sp.missedCallbacks ? `, ${sp.missedCallbacks} missed calls to call back` : ""}`}
              className={cn("inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs hover:bg-muted", away ? "text-warning" : "text-muted-foreground")}
            >
              <span className={cn("size-2 rounded-full", dot)} />
              <Phone className="size-3.5" />
              {away && <span className="font-semibold">Away</span>}
              {badge > 0 && <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1 text-[10.5px] font-bold text-sidebar">{badge}</span>}
            </button>
          )}
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[300px] space-y-3 p-3.5">
          {ringingIn && sp.incoming && (
            <div className="space-y-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-success">Incoming call</div>
              <div className="flex items-center gap-2.5">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">{sp.incoming.known ? initialsOf(sp.incoming.label || "") : "?"}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-semibold leading-tight">{sp.incoming.label || "Unknown number"}</span>
                  <span className="block truncate text-xs text-muted-foreground">{[sp.incoming.sub, sp.incoming.from_display].filter(Boolean).join(" · ")}</span>
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <StatusPill tone={sp.incoming.why === "OWNER" ? "success" : sp.incoming.why === "TEAM" ? "warning" : "neutral"}>
                  {sp.incoming.why === "OWNER" ? "You own this lead" : sp.incoming.why === "TEAM" ? `Team call${sp.incoming.owner_name ? ` · owner ${sp.incoming.owner_name}` : ""}` : sp.incoming.known ? "Overflow call" : "Unknown number"}
                </StatusPill>
                {sp.incoming.conflict && <StatusPill tone="warning">Same number on {1 + sp.incoming.other_names.length} records</StatusPill>}
              </div>
              {sp.incoming.conflict && sp.incoming.other_names.length > 0 && <p className="text-xs text-muted-foreground">Also: {sp.incoming.other_names.join(", ")}</p>}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const inc = sp.incoming;
                    await sp.decline();
                    if (inc?.why === "OWNER" && inc.known) success("Sent to your voicemail", `${inc.label} can leave a message. A callback task is waiting for you.`);
                  }}
                >
                  Decline
                </Button>
                <Button type="button" size="sm" onClick={() => void sp.answer()}>
                  <Phone className="mr-1.5 size-4" />
                  Answer
                </Button>
              </div>
              <p className="text-right text-[11.5px] text-muted-foreground">{sp.incoming.why === "OWNER" ? "Decline: they leave you a voicemail." : "Decline: it keeps ringing the others."}</p>
            </div>
          )}
          {sp.call && !ringingIn && (
            <div className="flex items-center gap-2.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">{initialsOf(sp.call.label)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold leading-tight">{sp.call.label}</span>
                {sp.call.sub && <span className="block truncate text-xs text-muted-foreground">{sp.call.sub}</span>}
              </span>
              <span className="font-mono text-sm font-semibold tabular-nums">{sp.phase === "dialing" ? "…" : sp.phase === "ringing" ? "Ringing" : mmss(sp.seconds)}</span>
            </div>
          )}

          {sp.call?.inbound && !sp.call.recordType && (sp.phase === "live" || ended) && !/^TEST$/.test(sp.call.recordType || "") && (
            <div className="rounded-lg border border-border bg-muted/40 p-2.5">
              <LinkCaller
                callId={sp.call.callId}
                onLinked={(r) => sp.updateCall({ recordType: r.type, label: r.label, sub: [r.sub, sp.call?.inbound?.fromDisplay].filter(Boolean).join(" · ") })}
                onSkip={ended ? () => { setOpen(false); sp.dismiss(); } : undefined}
                skipLabel="Done"
              />
            </div>
          )}

          {sp.waiting && (
            <p role="status" className="rounded-md bg-warning-soft px-2.5 py-1.5 text-xs text-warning">
              <strong className="font-semibold">{sp.waiting.label}</strong>
              {sp.waiting.sub ? ` · ${sp.waiting.sub}` : ""} · {sp.waiting.number} is calling now. They'll get voicemail, and a callback task is added for you.
            </p>
          )}

          {missedDuring > 0 && (
            <p className="rounded-md bg-warning-soft px-2.5 py-1.5 text-xs text-warning">
              While you were on this call, {missedDuring} customer{missedDuring === 1 ? "" : "s"} called and left a voicemail. Call them back from Missed · call back.
            </p>
          )}

          {inCall && (
            <>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="flex-1" onClick={sp.toggleMute} aria-pressed={sp.muted} disabled={sp.phase !== "live"}>
                  {sp.muted ? <MicOff className="mr-1.5 size-4" /> : <Mic className="mr-1.5 size-4" />}
                  {sp.muted ? "Muted" : "Mute"}
                </Button>
                <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => setKeypad((k) => !k)} aria-pressed={keypad} disabled={sp.phase !== "live"}>
                  <Grid3x3 className="mr-1.5 size-4" />
                  Keypad
                </Button>
                <Button type="button" size="sm" className="flex-1 bg-destructive text-white hover:bg-destructive/90" onClick={sp.hangup}>
                  <PhoneOff className="mr-1.5 size-4" />
                  {sp.phase === "live" ? "End" : "Cancel"}
                </Button>
              </div>
              {keypad && (
                <div className="grid grid-cols-3 gap-1.5" role="group" aria-label="Keypad">
                  {KEYS.map((k) => (
                    <button key={k} type="button" onClick={() => sp.sendDigit(k)} className="h-9 rounded-md border bg-card text-sm font-medium hover:bg-muted">
                      {k}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-center text-xs text-muted-foreground">{sp.recording ? "Recording is on. Use a headset to avoid echo." : "Use a headset to avoid echo."}</p>
            </>
          )}

          {wrapping && <p className="text-xs text-muted-foreground">No new calls ring you for {sp.wrapLeft} more seconds, so you can finish this. “Ready now” ends it early.</p>}
          {ended && (
            <div className="flex flex-wrap gap-2">
              {sp.call?.recordType && sp.call.recordType !== "TEST" && (
                <Button size="sm" onClick={() => sp.call && setOutcome({ callId: sp.call.callId, label: sp.call.label, sub: sp.call.sub })}>
                  Add outcome
                </Button>
              )}
              {wrapping && (
                <Button size="sm" variant="outline" onClick={sp.readyNow}>
                  Ready now
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => { setOpen(false); sp.dismiss(); }}>
                Done
              </Button>
            </div>
          )}

          {idle && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold">Your calling</span>
                <StatusPill tone={sp.phase === "offline" ? "neutral" : away ? "warning" : sp.rep.status === "AVAILABLE" ? "success" : "neutral"}>
                  {sp.phase === "connecting" ? "Connecting…" : sp.phase === "offline" ? "Offline" : away ? "Away" : sp.rep.status === "WRAP_UP" ? "Wrap-up" : sp.rep.status === "ON_CALL" ? "On a call" : "Available"}
                </StatusPill>
              </div>
              {sp.phase === "offline" || sp.phase === "connecting" ? (
                <p className="text-xs text-muted-foreground">
                  {sp.phase === "connecting" ? "Signing in to the phone line…" : "The softphone couldn't sign in, so customer calls can't ring you."}{" "}
                  <button type="button" onClick={() => go("/settings/my-calling")} className="font-semibold text-primary hover:underline">Open My calling to see why</button>
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold">Take incoming calls</span>
                      <span className="block text-xs text-muted-foreground">{away ? "Off: your customers ring your team." : "On: your customers ring you first."}</span>
                    </span>
                    {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                    <Switch
                      checked={!away && !choosing}
                      disabled={busy || (!sp.rep.allowPause && !away)}
                      aria-label="Take incoming calls"
                      onCheckedChange={(on) => {
                        if (on) { setChoosing(false); void run(() => sp.setAway(false)); } else setChoosing(true);
                      }}
                    />
                  </div>
                  {!sp.rep.allowPause && !away && <p className="text-xs text-muted-foreground">Your admin has turned off pausing incoming calls.</p>}
                  {choosing && !away && (
                    <div className="space-y-1.5 rounded-lg bg-muted/50 p-2.5">
                      <span className="flex items-center gap-1.5 text-xs font-semibold"><Moon className="size-3.5" /> Away for…</span>
                      <div className="flex flex-wrap gap-1.5">
                        {([["30 min", 30], ["1 hour", 60], ["Until I turn it on", null]] as const).map(([label, mins]) => (
                          <Button key={label} type="button" size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => void run(async () => { await sp.setAway(mins); setChoosing(false); })}>
                            {label}
                          </Button>
                        ))}
                      </div>
                      <p className="text-[11.5px] text-muted-foreground">Outgoing calls still work. It ends by itself at sign-out or the end of the day.</p>
                    </div>
                  )}
                  {away && (
                    <div className="flex items-center gap-2 rounded-lg bg-warning-soft px-2.5 py-2 text-xs text-warning">
                      <span className="flex-1">You're away {sp.rep.awayUntil ? `until ${clockTime(sp.rep.awayUntil)}` : "until you turn calls back on"}.</span>
                      <Button type="button" size="sm" className="h-7" disabled={busy} onClick={() => void run(() => sp.setAway(false))}>I'm back</Button>
                    </div>
                  )}
                </>
              )}

              {(sp.missedCallbacks > 0 || sp.pending.length > 0) && (
                <div className="space-y-1 border-t border-border/60 pt-2.5">
                  {sp.missedCallbacks > 0 && (
                    <button type="button" onClick={() => go("/calls?view=callback")} className="flex w-full items-center justify-between rounded-md px-1.5 py-1 text-left text-[13px] hover:bg-muted">
                      <span>{sp.missedCallbacks} missed call{sp.missedCallbacks === 1 ? "" : "s"} to call back</span>
                      <span className="text-muted-foreground">→</span>
                    </button>
                  )}
                  {sp.pending.length > 0 && (
                    <button type="button" onClick={() => go("/calls?view=needs_outcome")} className="flex w-full items-center justify-between rounded-md px-1.5 py-1 text-left text-[13px] hover:bg-muted">
                      <span>{sp.pending.length} call{sp.pending.length === 1 ? " needs" : "s need"} an outcome</span>
                      <span className="text-muted-foreground">→</span>
                    </button>
                  )}
                </div>
              )}

              {canTeam && team.length > 0 && (
                <div className="space-y-1 border-t border-border/60 pt-2.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">My team now</div>
                  {team.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 text-[13px]">
                      <span className={cn("size-2 shrink-0 rounded-full", TEAM_DOT[m.status])} />
                      <span className="min-w-0 flex-1 truncate">{m.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{teamText(m)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 border-t border-border/60 pt-2.5">
                <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => go("/calls")}>Open Calls</Button>
                <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => go("/settings/my-calling")}>My calling</Button>
              </div>
            </div>
          )}
          {sp.problem && <p role="alert" className="rounded-md bg-danger-soft px-2.5 py-1.5 text-xs text-destructive">{sp.problem}</p>}
        </PopoverContent>
      </Popover>
      <CallOutcomeDialog target={outcome} onClose={closeOutcome} />
    </>
  );
}
